import { useState } from 'react'
import { useToastController } from '@my/ui'
import {
  GroupCreationFormDetails,
  GroupCreationFormDetailsValues,
} from './group-creation-form-details'
import {
  GroupCreationFormCategories,
  GroupCreationFormCategoriesValues,
} from './group-creation-form-categories'
import { useCreateGroup, useUpdateGroup, useUploadGroupImages } from '../../commands'
import { useGroupCreationWizard } from './use-group-creation-wizard'
import {
  GroupCreationFormImages,
  GroupCreationFormImagesValues,
} from './group-creation-form-images'
import { GroupCreationPublish } from './group-creation-publish'
import { GroupCreationShare } from './group-creation-share'
import { getGroupInviteUrl } from '../../../../helpers'
import { GroupCreationModal } from './group-creation-modal'
import { useGroupCategoriesQuery, userGroupsQuery } from 'app/features/group/queries'
import { KeyboardAvoidingView } from 'react-native'

export interface GroupCreationWizardProps {
  user: any
  open: boolean
  onOpenChange: (value) => void
}

export const GroupCreationWizard = (props: GroupCreationWizardProps) => {
  const [groupInviteUrl, setGroupInviteUrl] = useState<string | null>(null)
  const [groupId, setGroupId] = useState<string | null>(null)
  const { formData, updateFormData, step, updateStep, reset } = useGroupCreationWizard()

  const toast = useToastController()

  const { data: groupCategoriesData } = useGroupCategoriesQuery()

  const [createGroup, { loading: isCreateGroupLoading, error: createGroupError }] = useCreateGroup()
  const [updateGroup, { loading: isUpdateGroupLoading, error: updateGroupError }] = useUpdateGroup()
  const [uploadGroupImages] = useUploadGroupImages(props.user)

  const handleDetailsSubmit = (values: GroupCreationFormDetailsValues) => {
    updateFormData(values)
    updateStep('categories')
  }

  const handleCategoriesSubmit = (values: GroupCreationFormCategoriesValues) => {
    updateFormData(values)
    updateStep('images')
  }

  const handleImagesSubmit = (values: GroupCreationFormImagesValues) => {
    updateFormData(values)
    updateStep('publish')
  }

  const handlePublishSuccess = () => {
    updateStep('share')
    toast.show('Your group has been created successfully')
  }

  const handlePublish = async () => {
    await createGroup({
      variables: {
        name: formData.name,
        visibility: formData.visibility,
        mainCategory: formData.mainCategory.value,
        subCategories: formData.subCategories.map((c) => c.value),
        description: formData.description || '',
      },
      refetchQueries: [
        {
          query: userGroupsQuery.query,
          variables: {
            ...userGroupsQuery.defaultVariables,
          },
        },
      ],
      onError: (error) => {
        toast.show(error.message || 'An error occurred while creating your group.', {
          burntOptions: {
            preset: 'error',
          },
        })
      },
      onCompleted: async (data) => {
        const { coverImage, profileImage } = formData

        // This should never happen, but just in case
        if (!data.createGroup?.group.id) {
          toast.show('An unexpected error occurred while creating your group. Please try again.', {
            burntOptions: {
              preset: 'error',
            },
          })
          return
        }

        setGroupInviteUrl(
          getGroupInviteUrl(data.createGroup?.group.id, data.createGroup?.inviteToken)
        )

        setGroupId(data.createGroup?.group.id)

        // If no images, finish early
        if (!coverImage && !profileImage) {
          handlePublishSuccess()
          return
        }

        // Update group images
        const groupId = data.createGroup?.group.id

        await uploadGroupImages({
          groupId,
          profileImage,
          coverImage,
          onCompleted: async (groupImageData) => {
            await updateGroup({
              variables: {
                id: groupId,
                coverImageId: groupImageData.coverImageId,
                profileImageId: groupImageData.profileImageId,
              },
              onError: () => {
                toast.show('An error occurred while uploading your images.', {
                  burntOptions: {
                    preset: 'error',
                  },
                })
              },
            })
          },
          onError: () => {
            toast.show('An error occurred while uploading your images.', {
              burntOptions: {
                preset: 'error',
              },
            })
          },
        })

        // Even if image upload fails, we still want to show the success message
        // since the group was created successfully
        handlePublishSuccess()
      },
    })
  }

  const handleOpenChange = (value: boolean) => {
    // Reset form when closing dialog in the success state
    if (step === 'share') {
      reset()
      props.onOpenChange(value)
      return
    }

    props.onOpenChange(value)
  }

  const groupCategoryOptions =
    groupCategoriesData?.groupCategories?.docs?.map((category) => ({
      label: category.name,
      value: category.id,
    })) || []

  return (
    <GroupCreationModal open={props.open} onOpenChange={handleOpenChange}>
      <KeyboardAvoidingView behavior="height" style={{ flex: 1 }}>
        {step === 'details' && (
          <GroupCreationFormDetails
            user={props.user}
            onClose={() => handleOpenChange(false)}
            onSubmit={handleDetailsSubmit}
            defaultValues={{
              name: formData.name,
              visibility: formData.visibility,
            }}
          />
        )}

        {step === 'categories' && (
          <GroupCreationFormCategories
            onBack={() => updateStep('details')}
            onSubmit={handleCategoriesSubmit}
            groupCategoriesOptions={groupCategoryOptions}
            defaultValues={{
              mainCategory: formData.mainCategory,
              subCategories: formData.subCategories,
              description: formData.description,
            }}
          />
        )}

        {step === 'images' && (
          <GroupCreationFormImages
            onBack={() => updateStep('categories')}
            onSubmit={handleImagesSubmit}
            toastController={toast}
            defaultValues={{
              coverImage: formData.coverImage,
              profileImage: formData.profileImage,
            }}
          />
        )}

        {step === 'publish' && (
          <GroupCreationPublish
            data={formData}
            onSubmit={handlePublish}
            onEdit={() => updateStep('details')}
            onBack={() => updateStep('images')}
            loading={isCreateGroupLoading || isUpdateGroupLoading}
            error={createGroupError?.message || updateGroupError?.message}
          />
        )}

        {step === 'share' && (
          <GroupCreationShare
            data={formData}
            toastController={toast}
            shareUrl={groupInviteUrl || ''}
            groupId={groupId || ''}
            onClose={() => handleOpenChange(false)}
          />
        )}
      </KeyboardAvoidingView>
    </GroupCreationModal>
  )
}
